import {
    Box,
    Button,
    IconButton,
    Skeleton,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material'
import { FC, useMemo, useState } from 'react'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import { ReplyScheduleDTO } from '../../api/gen/base'
import { useInfiniteReplyScheduleInThreadListQuery } from '../../api/hooks/useInfiniteReplyScheduleInThreadListQuery'
import { useInvalidateThreadListQuery } from '../../api/hooks/useInvalidateThreadListQuery'
import CalendarArrowUpIcon from '../../assets/icons/CalendarArrowUp.svg?react'
import PlusIcon from '../../assets/icons/Plus.svg?react'
import { ModalDialogWrapper } from '../../components/ModalDialogWrapper'
import { REGULAR_REQUESTS_LIMIT_FOR_THREAD } from '../../constants/regularRequests.constants'
import { useRequestsStore } from '../../store/storeRequests'
import { useWrapperStore } from '../../store/storeWrapper'
import {
    getRegularRequestFormFilesFromReplyContent,
    getTextContentFromReplyContent,
} from '../../utils/regularRequests'
import {
    RegularRequestCreateDialog,
    RegularRequestCreateDialogData,
} from '../RegularRequestCreateDialog'
import { RegularRequestDeleteDialog } from '../RegularRequestDeleteDialog'
import { RegularRequestEditDialog, RegularRequestEditDialogData } from '../RegularRequestEditDialog'
import { RegularRequestItem } from './components/RegularRequestItem'

type Props = {
    threadId: number
}

export const RegularRequestsDialog: FC<Props> = ({ threadId }) => {
    const { breakpoints } = useTheme()
    const isMobile = useMediaQuery(breakpoints.down('md'))

    const { setOpenRegularRequestsDialogThreadId } = useWrapperStore((state) => state)

    const { updateRegularRequestsCountInRequestDetail } = useRequestsStore((state) => state)

    const [regularRequestCreateDialogData, setRegularRequestCreateDialogData] =
        useState<RegularRequestCreateDialogData | null>(null)
    const [regularRequestEditDialogData, setRegularRequestEditDialogData] =
        useState<RegularRequestEditDialogData | null>(null)

    const [regularRequestDeleteDialogData, setRegularRequestDeleteDialogData] = useState<
        number | null
    >(null)

    const {
        data,
        isFetching,
        isFetchedAfterMount,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
        refetch,
    } = useInfiniteReplyScheduleInThreadListQuery(threadId)

    const invalidateThreadListQuery = useInvalidateThreadListQuery()

    const [infiniteRef] = useInfiniteScroll({
        loading: isFetching,
        hasNextPage,
        onLoadMore: fetchNextPage,
    })

    const handleClose = () => {
        setOpenRegularRequestsDialogThreadId(null)
    }

    const handleOpenCreateRegularRequestDialog = () => {
        setRegularRequestCreateDialogData({
            text: '',
            files: [],
            threadId,
        })
    }

    const handleCloseCreateRegularRequestDialog = () => {
        setRegularRequestCreateDialogData(null)
    }

    const handleOpenEditRegularRequestDialog = (request: ReplyScheduleDTO) => {
        const {
            reply: { content },
            timing,
        } = request

        const text = getTextContentFromReplyContent(content)

        setRegularRequestEditDialogData({
            text: text?.text ?? '',
            startDateTime: timing.send_time,
            repeatType: timing.repeat,
            repeatDays: timing.weekdays ?? undefined,
            files: getRegularRequestFormFilesFromReplyContent(content),
            threadId,
            replyScheduleId: request.id,
        })
    }

    const handleOpenDeleteRegularRequestDialog = (replyScheduleId: number) => {
        setRegularRequestDeleteDialogData(replyScheduleId)
    }

    const handleCloseEditRegularRequestDialog = () => {
        setRegularRequestEditDialogData(null)
    }

    const handleCloseDeleteRegularRequestDialog = () => {
        setRegularRequestDeleteDialogData(null)
    }

    const handleRefetchDataOnSuccess = () => {
        refetch()
        invalidateThreadListQuery()
        updateRegularRequestsCountInRequestDetail({ threadId })
    }

    const requestsList = useMemo(
        () =>
            data?.pages.reduce<ReplyScheduleDTO[]>((acc, page) => acc.concat(page.objects), []) ??
            [],
        [data],
    )

    return (
        <>
            <RegularRequestCreateDialog
                open={!!regularRequestCreateDialogData}
                initialData={regularRequestCreateDialogData}
                onClose={handleCloseCreateRegularRequestDialog}
                onSubmit={handleRefetchDataOnSuccess}
            />
            <RegularRequestEditDialog
                open={!!regularRequestEditDialogData && !regularRequestDeleteDialogData}
                initialData={regularRequestEditDialogData}
                onClose={handleCloseEditRegularRequestDialog}
                onDelete={handleOpenDeleteRegularRequestDialog}
                onSubmit={handleRefetchDataOnSuccess}
            />
            <RegularRequestDeleteDialog
                open={!!regularRequestDeleteDialogData}
                replyScheduleId={regularRequestDeleteDialogData}
                onClose={handleCloseDeleteRegularRequestDialog}
                onSuccess={() => {
                    handleRefetchDataOnSuccess()
                    handleCloseEditRegularRequestDialog()
                }}
            />
            <ModalDialogWrapper
                open
                handleClose={handleClose}
                title="Регулярные запросы"
                sx={{
                    '.MuiPaper-root': {
                        width: '894px',
                        maxWidth: '894px',
                        height: '100%',
                    },

                    [breakpoints.down('md')]: {
                        '.MuiPaper-root': {
                            m: 0,
                            borderRadius: 0,
                            height: '100%',
                            maxHeight: 'unset',
                        },
                    },
                }}
                headerActions={
                    requestsList.length < REGULAR_REQUESTS_LIMIT_FOR_THREAD && (
                        <>
                            {isMobile ? (
                                <IconButton
                                    size="large"
                                    sx={{
                                        backgroundColor: '#EFF6FF',
                                        color: '#006CFD',
                                    }}
                                    disabled={isFetching}
                                    onClick={handleOpenCreateRegularRequestDialog}>
                                    <PlusIcon />
                                </IconButton>
                            ) : (
                                <Button
                                    startIcon={<PlusIcon />}
                                    sx={{
                                        gap: 0,
                                        backgroundColor: '#EFF6FF',
                                    }}
                                    disabled={isFetching}
                                    onClick={handleOpenCreateRegularRequestDialog}>
                                    Добавить
                                </Button>
                            )}
                        </>
                    )
                }>
                {isFetching && !isFetchedAfterMount && !isFetchingNextPage ? (
                    <Stack gap={1}>
                        <Skeleton
                            ref={infiniteRef}
                            variant="rectangular"
                            width="100%"
                            height={112}
                            sx={{
                                borderRadius: '16px',
                                minHeight: '112px',
                            }}
                        />
                        <Skeleton
                            ref={infiniteRef}
                            variant="rectangular"
                            width="100%"
                            height={112}
                            sx={{
                                borderRadius: '16px',
                                minHeight: '112px',
                            }}
                        />
                        <Skeleton
                            ref={infiniteRef}
                            variant="rectangular"
                            width="100%"
                            height={112}
                            sx={{
                                borderRadius: '16px',
                                minHeight: '112px',
                            }}
                        />
                    </Stack>
                ) : (
                    <>
                        {requestsList.length ? (
                            <Stack gap={1}>
                                {requestsList.map((request) => (
                                    <RegularRequestItem
                                        key={request.id}
                                        request={request}
                                        onEdit={handleOpenEditRegularRequestDialog}
                                        onDelete={handleOpenDeleteRegularRequestDialog}
                                    />
                                ))}
                                {hasNextPage && (
                                    <Skeleton
                                        ref={infiniteRef}
                                        variant="rectangular"
                                        width="100%"
                                        height={112}
                                        sx={{
                                            borderRadius: '16px',
                                            minHeight: '112px',
                                        }}
                                    />
                                )}
                            </Stack>
                        ) : (
                            <Stack
                                direction="column"
                                alignItems="center"
                                justifyContent="center"
                                textAlign="center"
                                gap={2}
                                height="100%"
                                pb="52px">
                                <Box
                                    sx={{
                                        width: 'fit-content',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: '#F9FAFB',
                                        padding: '16px',
                                        borderRadius: '10px',
                                    }}>
                                    <CalendarArrowUpIcon />
                                </Box>
                                <Typography variant="h5" sx={{ textAlign: 'center' }}>
                                    Запросов по расписанию пока нет
                                </Typography>
                                <Typography
                                    variant="caption2"
                                    sx={{ color: '#5A5F72', maxWidth: '340px' }}>
                                    Здесь будут отображаться добавленные вами запросы по расписанию
                                </Typography>
                            </Stack>
                        )}
                    </>
                )}
            </ModalDialogWrapper>
        </>
    )
}
